import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Autocomplete,
    Pagination,
    IconButton,
    Grid,
    Divider,
    Tooltip,
    useTheme,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Avatar,
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import InventoryIcon from '@mui/icons-material/Inventory';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { Helmet } from "react-helmet";
import { useUser } from "../context/UserContext";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";
import BlockIcon from '@mui/icons-material/Block';
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import ConfirmDialog from "../components/ConfirmDialog";

function StatCard({ label, count, IconComponent, active = false, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                height: "100%",
                minWidth: { xs: '100%', sm: 200 },
                width: "100%",
                borderRadius: 2,
                cursor: onClick ? 'pointer' : 'default',
                background: active
                    ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.4) 0%, rgba(44, 83, 100, 0.4) 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: active ? '1px solid #4CA1AF' : '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: active ? '0 8px 32px rgba(76, 161, 175, 0.25)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
                    borderColor: 'rgba(76, 161, 175, 0.5)',
                },
            }}
        >
            <CardContent sx={{ minHeight: 80, p: 2 }}>
                <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                    <Box
                        sx={{
                            height: 48,
                            width: 48,
                            backgroundColor: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.15)',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            flexShrink: 0,
                            transition: 'all 0.25s ease',
                            boxShadow: active ? '0 4px 12px rgba(76, 161, 175, 0.4)' : 'none',
                        }}
                    >
                        <IconComponent sx={{ fontSize: 28, color: active ? '#FFFFFF' : '#4CA1AF' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: '#FFFFFF' }}>
                                {count}
                            </Typography>
                            {active && (
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 1,
                                        bgcolor: 'rgba(76, 161, 175, 0.3)',
                                        border: '1px solid rgba(76, 161, 175, 0.6)',
                                        color: '#64d2e2',
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Active
                                </Box>
                            )}
                        </Box>
                        <Typography variant="body2" noWrap sx={{ color: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)', fontWeight: active ? 600 : 400 }}>
                            {label}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function GiverItemRequests({ hideHeader = false }) {
    const { user, isSwitched, effectiveOrgId } = useUser();
    const muiTheme = useTheme();
    const isLight = muiTheme.palette.mode === 'light';
    const [sortOrder, setSortOrder] = useState('asc');
    const [loading, setLoading] = useState(true);
    const [applications, setApplications] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [organization, setOrganization] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null,
        severity: 'error'
    });

    // Filter states
    const [filterStatus, setFilterStatus] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Statistics
    const [totalApplications, setTotalApplications] = useState(0);
    const [pendingApplications, setPendingApplications] = useState(0);
    const [approvedApplications, setApprovedApplications] = useState(0);
    const [rejectedApplications, setRejectedApplications] = useState(0);

    // View dialog
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);

    /// Block user dialog
    const [openBlockDialog, setOpenBlockDialog] = useState(false);
    const [userToBlock, setUserToBlock] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';

    const statuses = ["Pending", "Approved", "Rejected"];

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        fetchCounts();
    }, [effectiveOrgId, organization]);

    useEffect(() => {
        fetchApplications();
    }, [page, filterStatus, searchQuery, organization, effectiveOrgId, fromDate, toDate]);

    const fetchOrganizations = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}roles-organizations`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });
            } else {
                setOrganizations(data.organizations || []);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const fetchCounts = async () => {
        try {
            const token = localStorage.getItem('token');
            const targetOrg = effectiveOrgId || (organization && organization.id !== 0 ? organization.id : null);
            const requestBody = {};
            if (targetOrg) requestBody.org_id = targetOrg;

            const response = await fetch(`${API_URL}all-giver-item-applications`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            const data = await response.json();
            if (data && Array.isArray(data.data)) {
                const all = data.data;
                setTotalApplications(all.length);
                setPendingApplications(all.filter(app => app.status === 'Pending').length);
                setApprovedApplications(all.filter(app => app.status === 'Approved').length);
                setRejectedApplications(all.filter(app => app.status === 'Rejected').length);
            }
        } catch (error) {
            console.error('Error fetching application counts:', error);
        }
    };

    const fetchApplications = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const targetOrg = effectiveOrgId || (organization && organization.id !== 0 ? organization.id : null);

            // Build request body
            const requestBody = {};
            if (filterStatus && filterStatus !== 'All') requestBody.status = filterStatus;
            if (searchQuery) requestBody.search = searchQuery;
            if (targetOrg) requestBody.org_id = targetOrg;

            const response = await fetch(`${API_URL}all-giver-item-applications`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });
            } else {
                let applicationsData = Array.isArray(data?.data) ? data.data : [];
                if (fromDate) {
                    const from = new Date(fromDate);
                    applicationsData = applicationsData.filter(app => new Date(app.created_at) >= from);
                }
                if (toDate) {
                    const to = new Date(toDate);
                    to.setHours(23, 59, 59, 999);
                    applicationsData = applicationsData.filter(app => new Date(app.created_at) <= to);
                }

                setApplications(applicationsData);

                // Calculate pagination (12 items per page)
                const itemsPerPage = 12;
                setTotalPages(Math.ceil(applicationsData.length / itemsPerPage));
            }
        } catch (error) {
            console.error('Error fetching applications:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleViewApplication = (application) => {
        setSelectedApplication(application);
        setOpenViewDialog(true);
    };

    const handleViewDialogClose = () => {
        setOpenViewDialog(false);
        setSelectedApplication(null);
    };

    const handleUpdateStatus = async (applicationId, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}update-application-status`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: applicationId,
                    status: newStatus,
                    user_id: user.id
                }),
            });

            const data = await response.json();

            if (data.success && data.status === 200) {
                setToast({ open: true, message: `Application ${newStatus.toLowerCase()} successfully`, severity: 'success' });
                fetchApplications();
            } else {
                setToast({ open: true, message: data.message || 'Failed to update status', severity: 'error' });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
        }
    };

    const handleConfirmRejectApplication = (applicationId, applicantName) => {
        setConfirmDialog({
            open: true,
            title: "Decline / Reject Request",
            message: `Are you sure you want to decline / reject this request from ${applicantName || 'this user'}?`,
            severity: "error",
            onConfirm: () => {
                handleUpdateStatus(applicationId, 'Rejected');
                setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
                if (openViewDialog) handleViewDialogClose();
            }
        });
    };

    const handleClearFilters = () => {
        setFilterStatus("");
        setSearchQuery("");
        setOrganization(null);
        setPage(1);
    };

    // Paginate applications
    const itemsPerPage = 12;
    const paginatedApplications = applications.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];


    const handleBlockUnblockUser = (userId, currentBlockedStatus, userName) => {
        setUserToBlock({ userId, currentBlockedStatus, userName });
        setOpenBlockDialog(true);
    };

    const confirmBlockUnblockUser = async () => {
        try {
            const { userId, currentBlockedStatus } = userToBlock;
            const newBlockedStatus = currentBlockedStatus === 'yes' ? 'no' : 'yes';
            const token = localStorage.getItem('token');

            const response = await fetch(`${API_URL}block-unblock-user`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userId,
                    blocked_status: newBlockedStatus
                }),
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }

                setToast({ open: true, message: data?.message || 'Failed to update user status', severity: "error" });
            } else {
                setToast({
                    open: true,
                    message: newBlockedStatus === 'yes' ? 'User blocked successfully' : 'User unblocked successfully',
                    severity: 'success'
                });
                fetchApplications();
            }

            setOpenBlockDialog(false);
            setUserToBlock(null);

        } catch (error) {
            console.error('Error blocking/unblocking user:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
            setOpenBlockDialog(false);
            setUserToBlock(null);
        }
    };

    return (
        <>
            {!hideHeader && (
                <Helmet>
                    <title>Giver Item Requests - GPN</title>
                </Helmet>
            )}

            <Box p={hideHeader ? 0 : { xs: 1, sm: 2, md: 3 }}>
                {/* Header */}
                {!hideHeader && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Giver Item Requests
                        </Typography>
                    </Box>
                )}

                                {/* Compact Unified Toolbar */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        mb: 2.5,
                        borderRadius: 2.5,
                        background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.04)",
                        border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.08)",
                        backdropFilter: "blur(20px)",
                    }}
                >
                    {/* Row 1: Title, Count, Search, Sort & Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            pb: 1.25,
                            borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255,255,255,0.06)",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                                Giver Requests
                            </Typography>
                            <Chip
                                label={
                                filterStatus === "Pending"
                                    ? `${pendingApplications} Pending`
                                    : filterStatus === "Approved"
                                    ? `${approvedApplications} Approved`
                                    : filterStatus === "Rejected"
                                    ? `${rejectedApplications} Rejected`
                                    : `${totalApplications || applications.length} Total`
                            }
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.2)",
                                    color: isLight ? "#0D9488" : "#64d2e2",
                                }}
                            />
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <TextField
                                size="small"
                                placeholder="Search requests..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
                            />

                            <Select
                                size="small"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                displayEmpty
                                renderValue={(val) => `Sort: ${val === 'asc' ? 'A → Z' : val === 'desc' ? 'Z → A' : 'Newest'}`}
                                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
                            >
                                <MenuItem value="asc">Name: A → Z (Alphabetical)</MenuItem>
                                <MenuItem value="desc">Name: Z → A</MenuItem>
                                <MenuItem value="newest">Date: Newest First</MenuItem>
                            </Select>

                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleClearFilters}
                                sx={{ textTransform: "none", fontSize: "0.8rem", py: 0.5, px: 1.2 }}
                            >
                                Clear
                            </Button>

                            {!hideHeader && (
                                <Tooltip title="Settings">
                                    <IconButton size="small" onClick={() => setSettingsOpen(true)} sx={{ color: "#64d2e2" }}>
                                        <SettingsIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    {/* Row 2: Status Triage Chips & Secondary Filters */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.25,
                            pt: 1.25,
                        }}
                    >
                        {/* Status Triage Chips */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            {[
                                { key: "", label: "All", count: totalApplications || applications.length },
                                { key: "Pending", label: "Pending", count: pendingApplications },
                                { key: "Approved", label: "Approved", count: approvedApplications },
                                { key: "Rejected", label: "Rejected", count: rejectedApplications },
                            ].map((tab) => {
                                const isActive = (tab.key === "" && (filterStatus === "" || filterStatus === "All")) || filterStatus === tab.key;
                                return (
                                    <Chip
                                        key={tab.label}
                                        label={`${tab.label} (${tab.count})`}
                                        size="small"
                                        onClick={() => {
                                            setFilterStatus(tab.key);
                                            setPage(1);
                                        }}
                                        sx={{
                                            cursor: "pointer",
                                            fontWeight: isActive ? 700 : 500,
                                            fontSize: "0.78rem",
                                            height: 26,
                                            bgcolor: isActive
                                                ? (isLight ? "#0D9488" : "#4CA1AF")
                                                : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)"),
                                            color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.7)"),
                                            "&:hover": {
                                                bgcolor: isActive
                                                    ? (isLight ? "#0F766E" : "#3b8b98")
                                                    : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.12)"),
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>

                        {/* Secondary Filters */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            {user?.role === 1 && !isSwitched && (
                                <Autocomplete
                                    size="small"
                                    value={organization}
                                    onChange={(event, newValue) => {
                                        setOrganization(newValue);
                                        setPage(1);
                                    }}
                                    options={orgOptions}
                                    getOptionLabel={(option) => option?.org_name || ""}
                                    renderInput={(params) => <TextField {...params} placeholder="Organization" />}
                                    sx={{ width: 170, "& .MuiInputBase-input": { py: "2px !important", fontSize: "0.8rem" } }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                />
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Loading State */}
                {loading && <SectionLoader open={loading} />}

                {/* Requests Table List View */}
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.25)',
                        bgcolor: isLight ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)',
                        boxShadow: isLight ? '0 2px 10px rgba(0, 0, 0, 0.04)' : '0 4px 20px rgba(0, 0, 0, 0.25)',
                        width: '100%',
                    }}
                >
                    <Table sx={{ minWidth: { xs: 0, lg: 900 } }}>
                        <TableHead sx={{ bgcolor: isLight ? '#F1F5F9' : '#1a2636' }}>
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 800, width: 55, color: isLight ? '#475569' : '#E2E8F0' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: { xs: 0, lg: 200 }, color: isLight ? '#475569' : '#E2E8F0' }}>Requested Item</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: { xs: 0, lg: 170 }, color: isLight ? '#475569' : '#E2E8F0' }}>Applicant / Contact</TableCell>
                                <TableCell className="col-tablet-hide" sx={{ fontWeight: 800, minWidth: { xs: 0, lg: 240 }, color: isLight ? '#475569' : '#E2E8F0' }}>Reason / Purpose</TableCell>
                                {user?.role === 1 && (
                                    <TableCell className="col-tablet-hide" sx={{ fontWeight: 800, minWidth: { xs: 0, lg: 140 }, color: isLight ? '#475569' : '#E2E8F0' }}>Organization</TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 800, minWidth: { xs: 0, lg: 120 }, color: isLight ? '#475569' : '#E2E8F0' }}>Status</TableCell>
                                <TableCell className="col-tablet-hide" sx={{ fontWeight: 800, minWidth: { xs: 0, lg: 120 }, color: isLight ? '#475569' : '#E2E8F0' }}>Date</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 800, minWidth: { xs: 0, lg: 140 }, color: isLight ? '#475569' : '#E2E8F0' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedApplications.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={user?.role === 1 ? 8 : 7} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary" variant="subtitle1" fontWeight={600}>
                                            No requests found.
                                        </Typography>
                                        <Typography color="text.secondary" variant="caption">
                                            Try adjusting your filters or search query.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedApplications.map((application, idx) => {
                                    const applicantName = application.applicant_name || 'Unknown Applicant';

                                    return (
                                        <TableRow
                                            key={application.id || idx}
                                            hover
                                            onClick={() => handleViewApplication(application)}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s ease',
                                                '&:hover': {
                                                    bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                                                },
                                                '& td': {
                                                    borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                                                    py: 1.5,
                                                    color: isLight ? '#1E293B' : '#F1F5F9',
                                                }
                                            }}
                                        >
                                            <TableCell align="center" sx={{ fontWeight: 700, color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>
                                                {((page - 1) * itemsPerPage) + idx + 1}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#F8FAFC' }}>
                                                    {application.item_title || 'Item Request'}
                                                </Typography>
                                                {application.give_items?.category && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {application.give_items.category}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#0D9488', fontSize: '0.8rem', fontWeight: 700 }}>
                                                        {applicantName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Box minWidth={0}>
                                                        <Typography variant="body2" fontWeight={600} noWrap sx={{ color: isLight ? '#0F172A' : '#F8FAFC' }}>
                                                            {applicantName}
                                                        </Typography>
                                                        {application.applicant_phone && (
                                                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                                {application.applicant_phone}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        maxWidth: 320,
                                                        lineHeight: 1.35
                                                    }}
                                                >
                                                    {application.reason || 'No specific reason provided.'}
                                                </Typography>
                                            </TableCell>
                                            {user?.role === 1 && (
                                                <TableCell>
                                                    {application.origanisation?.org_name ? (
                                                        <Chip
                                                            label={application.origanisation.org_name}
                                                            size="small"
                                                            color="secondary"
                                                            variant="outlined"
                                                            sx={{ borderColor: '#4CA1AF', color: '#4CA1AF', fontWeight: 600, fontSize: '0.72rem' }}
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Chip
                                                    label={application.status}
                                                    size="small"
                                                    color={
                                                        application.status === 'Pending' ? 'warning' :
                                                            application.status === 'Accepted' ? 'success' : 'error'
                                                    }
                                                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <CalendarTodayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {application.created_at ? new Date(application.created_at).toLocaleDateString() : '—'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewApplication(application)}
                                                            aria-label="View"
                                                            sx={{ color: '#4CA1AF', '&:hover': { background: 'rgba(76, 161, 175, 0.1)' } }}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {application.status === 'Pending' && (
                                                        <>
                                                            <Tooltip title="Approve Request">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleUpdateStatus(application.id, 'Accepted')}
                                                                    aria-label="Approve"
                                                                    sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                                                                >
                                                                    <CheckCircleIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Decline / Reject Request">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleConfirmRejectApplication(application.id, application.applicant_name)}
                                                                    aria-label="Decline / Reject Request"
                                                                    sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                                                >
                                                                    <CancelIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}

                                                    {application.status === 'Accepted' && (
                                                        <Tooltip title="Decline / Reject Request">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleConfirmRejectApplication(application.id, application.applicant_name)}
                                                                aria-label="Decline / Reject Request"
                                                                sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {application.status === 'Rejected' && (
                                                        <Tooltip title="Approve Request">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleUpdateStatus(application.id, 'Accepted')}
                                                                aria-label="Approve"
                                                                sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                                                            >
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {application.applicant_id && application.applicant_blocked_status !== undefined && (
                                                        <Tooltip title={application.applicant_blocked_status === 'yes' ? "Unblock User" : "Block User"}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleBlockUnblockUser(
                                                                    application.applicant_id,
                                                                    application.applicant_blocked_status,
                                                                    application.applicant_name
                                                                )}
                                                                aria-label={application.applicant_blocked_status === 'yes' ? "Unblock User" : "Block User"}
                                                                sx={{
                                                                    color: application.applicant_blocked_status === 'yes' ? '#059669' : '#DC2626',
                                                                    '&:hover': {
                                                                        background: application.applicant_blocked_status === 'yes' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(220, 38, 38, 0.1)'
                                                                    }
                                                                }}
                                                            >
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3} width="100%">
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}

                {/* View Application Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleViewDialogClose}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            background: 'linear-gradient(145deg, #15222E 0%, #1A2E3B 100%)',
                            border: '1px solid rgba(76, 161, 175, 0.35)',
                            borderRadius: '16px',
                            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.5)',
                            color: '#FFFFFF',
                        }
                    }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <PendingActionsIcon sx={{ color: '#4CA1AF', fontSize: 28 }} />
                            <Box>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF', lineHeight: 1.2 }}>
                                    Request Details
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                    Share & Accept • Beneficiary Item Application
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleViewDialogClose} sx={{ color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.08)' } }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ py: 2.5 }}>
                        {selectedApplication && (
                            <Box>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF' }}>
                                        {selectedApplication.item_title || 'Item Request'}
                                    </Typography>
                                    <Chip
                                        label={selectedApplication.status}
                                        size="small"
                                        sx={{
                                            bgcolor: selectedApplication.status === 'Accepted' || selectedApplication.status === 'Approved' ? 'rgba(16, 185, 129, 0.2)' :
                                                selectedApplication.status === 'Pending' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                            color: selectedApplication.status === 'Accepted' || selectedApplication.status === 'Approved' ? '#10B981' :
                                                selectedApplication.status === 'Pending' ? '#F59E0B' : '#EF4444',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            fontWeight: 600,
                                        }}
                                    />
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                                Applicant Name
                                            </Typography>
                                            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                                <PersonIcon sx={{ fontSize: 16, color: '#4CA1AF' }} />
                                                <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                                                    {selectedApplication.applicant_name || 'Unknown'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {selectedApplication.applicant_phone && (
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                                    Phone
                                                </Typography>
                                                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                                    <PhoneIcon sx={{ fontSize: 16, color: '#4CA1AF' }} />
                                                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                                        {selectedApplication.applicant_phone}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                            <Typography variant="subtitle2" sx={{ color: '#64d2e2', fontWeight: 600, mb: 1 }}>
                                                Reason for Request
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                                {selectedApplication.reason || 'No specific reason provided.'}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                            Requested On
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                            {selectedApplication.created_at ? new Date(selectedApplication.created_at).toLocaleString() : 'N/A'}
                                        </Typography>
                                    </Grid>

                                    {selectedApplication.updated_at && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                                Last Updated
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                                                {new Date(selectedApplication.updated_at).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1.2} flexWrap="wrap">
                            {selectedApplication && selectedApplication.status === 'Pending' && (
                                <>
                                    <Button
                                        onClick={() => {
                                            handleUpdateStatus(selectedApplication.id, 'Accepted');
                                            handleViewDialogClose();
                                        }}
                                        variant="outlined"
                                        startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            color: '#10B981',
                                            borderColor: 'rgba(16, 185, 129, 0.6)',
                                            bgcolor: 'rgba(16, 185, 129, 0.12)',
                                            borderRadius: '8px',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            px: 1.8,
                                            '&:hover': { borderColor: '#10B981', bgcolor: 'rgba(16, 185, 129, 0.25)', color: '#FFFFFF' }
                                        }}
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        onClick={() => handleConfirmRejectApplication(selectedApplication.id, selectedApplication.applicant_name)}
                                        variant="outlined"
                                        startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            color: '#EF4444',
                                            borderColor: 'rgba(239, 68, 68, 0.6)',
                                            bgcolor: 'rgba(239, 68, 68, 0.12)',
                                            borderRadius: '8px',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            px: 1.8,
                                            '&:hover': { borderColor: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.25)', color: '#FFFFFF' }
                                        }}
                                    >
                                        Decline
                                    </Button>
                                </>
                            )}
                            {selectedApplication && selectedApplication.status === 'Accepted' && (
                                <Button
                                    onClick={() => handleConfirmRejectApplication(selectedApplication.id, selectedApplication.applicant_name)}
                                    variant="outlined"
                                    startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        color: '#EF4444',
                                        borderColor: 'rgba(239, 68, 68, 0.6)',
                                        bgcolor: 'rgba(239, 68, 68, 0.12)',
                                        borderRadius: '8px',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        px: 1.8,
                                        '&:hover': { borderColor: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.25)', color: '#FFFFFF' }
                                    }}
                                >
                                    Decline
                                </Button>
                            )}
                            {selectedApplication && selectedApplication.status === 'Rejected' && (
                                <Button
                                    onClick={() => {
                                        handleUpdateStatus(selectedApplication.id, 'Accepted');
                                        handleViewDialogClose();
                                    }}
                                    variant="outlined"
                                    startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        color: '#10B981',
                                        borderColor: 'rgba(16, 185, 129, 0.6)',
                                        bgcolor: 'rgba(16, 185, 129, 0.12)',
                                        borderRadius: '8px',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        px: 1.8,
                                        '&:hover': { borderColor: '#10B981', bgcolor: 'rgba(16, 185, 129, 0.25)', color: '#FFFFFF' }
                                    }}
                                >
                                    Approve
                                </Button>
                            )}
                        </Box>
                        <Button
                            onClick={handleViewDialogClose}
                            variant="outlined"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.8)',
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                borderRadius: '8px',
                                '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.05)' }
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>



                                {/* Block/Unblock Confirmation Dialog */}
                <Dialog
                    open={openBlockDialog}
                    onClose={() => setOpenBlockDialog(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>
                        <Typography variant="h6" fontWeight="bold">
                            Confirm {userToBlock?.currentBlockedStatus === 'yes' ? 'Unblock' : 'Block'} User
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" gutterBottom>
                            Are you sure you want to {userToBlock?.currentBlockedStatus === 'yes' ? 'unblock' : 'block'} <strong>{userToBlock?.userName}</strong>?
                        </Typography>
                        {userToBlock?.currentBlockedStatus !== 'yes' && (
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                ⚠️ This will prevent the user from accessing Share & Accept features on the platform.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenBlockDialog(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmBlockUnblockUser}
                            variant="contained"
                            color={userToBlock?.currentBlockedStatus === 'yes' ? 'success' : 'error'}
                        >
                            {userToBlock?.currentBlockedStatus === 'yes' ? 'Unblock' : 'Block'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Toast Notifications */}
                <Toast toast={toast} setToast={setToast} />

                {/* Confirm Dialog */}
                <ConfirmDialog
                    open={confirmDialog.open}
                    title={confirmDialog.title}
                    message={confirmDialog.message}
                    onConfirm={confirmDialog.onConfirm}
                    onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
                    severity={confirmDialog.severity || "error"}
                />

                {/* Module Settings Modal */}
                <ModuleSettingsModal
                    open={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    moduleKey="giverItemRequests"
                    moduleTitle="Giver Item Requests"
                />
            </Box>
        </>
    );
}
